import type { CartItem } from './cartStorage';

/**
 * Single source of truth for "what is due today" on the cart and checkout pages.
 * Extracted verbatim from CheckoutPage so both pages compute identical totals.
 */

export type ShippingMethod = 'express' | 'overnight' | 'overnight-saturday';

export interface PlanPricingInput {
  monthlyPrice: number;
  annualTotal: number | null;
  /** Amount charged today for the plan: annualTotal for annual plans, monthlyPrice for monthly. */
  todayTotal: number;
  billingCycle: string;
}

export interface OrderTotalsInput {
  cartItems: CartItem[];
  /** Pricing for the product plan in the cart. null when no product or pricing has not loaded. */
  planPricing: PlanPricingInput | null;
  shippingMethod?: string | null;
  /** Legacy fall-detection add-on flag passed via route state from ProductDetailPage. */
  fallDetection?: boolean;
}

export interface OrderTotals {
  hasProduct: boolean;
  productTodayTotal: number;
  accessoriesTotal: number;
  fallDetectionAddOn: number;
  accessoryShippingFee: number;
  planShippingFee: number;
  shippingFeeToday: number;
  subtotal: number;
  orderTotalToday: number;
  hasAnnualPlan: boolean;
  shippingMethod: ShippingMethod;
}

// Accessory-only shipping: per-item fees, use highest when multiple
export const ACCESSORY_SHIPPING: Record<string, number> = {
  'accessory-3': 5,
  'accessory-4': 10,
  'accessory-9': 10,
  'accessory-10': 5,
};

export const PLAN_SHIPPING_FEES: Record<ShippingMethod, number> = {
  express: 0,
  overnight: 59.99,
  'overnight-saturday': 79.99,
};

export const FALL_DETECTION_ADD_ON = { annual: 60, monthly: 5 };

/**
 * Build plan pricing from a /api/products pricing plan the same way the
 * server's /api/products/plans/:id endpoint does, so the cart page prices a
 * plan identically to checkout.
 */
export function planPricingFromPlan(plan: {
  monthlyPrice: number;
  annualTotal: number | null;
  billingCycle: string;
}): PlanPricingInput {
  const todayTotal = plan.billingCycle === 'annual' ? (plan.annualTotal ?? 0) : plan.monthlyPrice;
  return {
    monthlyPrice: plan.monthlyPrice,
    annualTotal: plan.annualTotal,
    todayTotal,
    billingCycle: plan.billingCycle,
  };
}

export function formatMoney(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

export function normalizeShippingMethod(method?: string | null): ShippingMethod {
  const m = String(method || 'express').trim().toLowerCase();
  if (m === 'overnight') return 'overnight';
  if (m === 'overnight-saturday') return 'overnight-saturday';
  return 'express';
}

export function computeOrderTotals(input: OrderTotalsInput): OrderTotals {
  const { cartItems, planPricing, fallDetection } = input;

  const productItems = cartItems.filter(item => item.type === 'product');
  const accessoryItems = cartItems.filter(item => item.type === 'accessory');
  const hasProduct = productItems.length > 0;

  const accessoriesTotal = accessoryItems.reduce((sum, item) => sum + (item.price || 0), 0);
  const productTodayTotal = planPricing ? (planPricing.todayTotal || 0) : 0;

  const fallDetectionAddOn =
    fallDetection && planPricing
      ? (planPricing.billingCycle === 'annual' ? FALL_DETECTION_ADD_ON.annual : FALL_DETECTION_ADD_ON.monthly)
      : 0;

  const hasAnnualPlan =
    planPricing?.billingCycle === 'annual' ||
    cartItems.some(item => item.billingCycle === 'annual');

  const accessoryShippingFee =
    !hasProduct && accessoryItems.length > 0
      ? (() => {
          const fees = accessoryItems
            .map(a => (a.accessoryId && ACCESSORY_SHIPPING[a.accessoryId]) || 0)
            .filter(f => f > 0);
          return fees.length > 0 ? Math.max(...fees) : 0;
        })()
      : 0;

  const shippingMethod = normalizeShippingMethod(input.shippingMethod);
  const planShippingFee = hasProduct ? PLAN_SHIPPING_FEES[shippingMethod] : 0;
  const shippingFeeToday = hasProduct ? planShippingFee : accessoryShippingFee;

  const subtotal = productTodayTotal + accessoriesTotal;
  const orderTotalToday = subtotal + fallDetectionAddOn + shippingFeeToday;

  return {
    hasProduct,
    productTodayTotal,
    accessoriesTotal,
    fallDetectionAddOn,
    accessoryShippingFee,
    planShippingFee,
    shippingFeeToday,
    subtotal,
    orderTotalToday,
    hasAnnualPlan,
    shippingMethod,
  };
}
